import { useState, useEffect, useRef, isValidElement } from 'react';
import PropTypes from 'prop-types';

// Material UI
import { TextField, Icon, IconButton, InputAdornment } from '@mui/material';

// Utils
import { isEmptyOrNullObject } from '@utils/validations';
import { intWithCommas, floatWithCommas } from '@utils/Utilities';

export const TextFieldTK = (props) => {
  const {
    size = 'small',
    label = '',
    name = 'default_name',
    value = '',
    onChange = () => {},
    onBlur = () => {},
    noSpaces = false,
    useTrim = false, // Elimina los espacios en onBlur y OnChange cuando se envía el valor al padre
    isNumber = false, // Solo permite números
    isFloat = false, // Solo permite números enteros y decimales
    isPassword = false, // Muestra un icono para mostrar/ocultar la contraseña
    toUpperCase = false,
    toLowerCase = false,
    toCamelCase = false,
    useCommas = false, // Da el formato de comas (solo de vista)
    maxLength = 999999,
    maxNumber = null,
    usePlaceholder = '', // Aparece con el icon si el espacio lo permite, y se retorna asi mismo + el value
    validation = () => true, // Función de validación personalizada
    delay = 500, // Tiempo de espera para enviar el valor al padre
    icon = {}, // icon = { position: 'start' | 'end', icon: 'icon_name' | <Icon>, text: 'text' }
    button = {}, // button = { positionByIcon: 'start' | 'end', icon: 'icon_name' | <Icon>, onClick: () => {}}
    ...others
  } = props;

  const isSelect = Boolean(others?.select);

  const [localValue, setLocalValue] = useState(value);
  const [showPassword, setShowPassword] = useState(false);

  const inputRef = useRef(null);
  const timeoutIdRef = useRef(null);

  const formatPreValidations = (value) => {
    if (!value) return value;
    if ((isNumber || isFloat) && useCommas) value = value?.replace(/,/g, '');
    if (isFloat && value.startsWith('.')) value = '0' + value;
    return value;
  };

  const formatValueLocal = (value) => {
    if (!value) return value;
    if (toUpperCase) value = ('' + value)?.toUpperCase();
    if (toLowerCase) value = ('' + value)?.toLowerCase();
    if (toCamelCase) value = ('' + value)?.toLowerCase()?.replace(/(?:^|\s)\p{L}/gu, (l) => l?.toUpperCase());
    return value;
  };

  const formatValueToSend = (value) => {
    if (!value) return value;
    if (useTrim) value = ('' + value)?.trim();
    if (usePlaceholder) value = usePlaceholder + value;
    return value;
  };

  const localOnChange = ({ target: { value } }) => {
    value = formatPreValidations(value);

    let isValid = value?.length <= parseInt(maxLength);
    if (isValid && isNumber) isValid = /^\d*$/.test(value);
    if (isValid && isFloat) isValid = /^(0(\.[\d]{0,2})?|[1-9]\d*(\.\d{0,2})?)?$/.test(value);
    if (isValid && noSpaces) isValid = !value?.includes(' ');
    if (isValid && maxNumber) isValid = parseFloat(value || 0) <= parseFloat(maxNumber);

    if (isValid && validation(value) && !isSelect) {
      value = formatValueLocal(value);
      setLocalValue(value);

      if (timeoutIdRef?.current) clearTimeout(timeoutIdRef.current);
      value = formatValueToSend(value);

      timeoutIdRef.current = setTimeout(() => onChange({ target: { name, value } }), delay);
    } else if (isSelect) onChange({ target: { name, value } });
  };

  const localOnBlur = (e) => {
    let valueTmp = localValue;
    if (!isSelect) {
      if (timeoutIdRef?.current) clearTimeout(timeoutIdRef.current);
      valueTmp = formatValueToSend(valueTmp);

      const formatValue = usePlaceholder ? value?.replace(usePlaceholder, '') : value;
      if (formatValue !== localValue) onChange({ target: { name, value: valueTmp } });
    }
    onBlur({ target: { name, value: valueTmp } });
  };

  useEffect(() => {
    if (value !== localValue) setLocalValue(usePlaceholder ? value?.replace(usePlaceholder, '') : value);
    // eslint-disable-next-line
  }, [value]);

  const getValue = (value) => {
    if (useCommas) {
      if (isNumber) value = intWithCommas(value);
      if (isFloat) value = floatWithCommas(value);
    }
    if (usePlaceholder) value = value?.replace(usePlaceholder, '');
    return value;
  };

  return (
    <TextField
      inputRef={inputRef}
      size={size}
      label={label}
      name={name}
      value={getValue(localValue)}
      onChange={localOnChange}
      onBlur={localOnBlur}
      InputProps={{
        ...InputPropsFunc({
          inputRef,
          isPassword,
          setShowPassword,
          showPassword,
          icon,
          button,
          usePlaceholder,
          InputProps: others.InputProps,
        }),
        inputProps: { style: { flex: 1, minWidth: 140 } },
        style: { overflow: 'hidden' },
      }}
      type={isPassword ? (showPassword ? others?.type : 'password') : others?.type}
      {...others}
    />
  );
};

const InputPropsFunc = (props) => {
  const { isPassword, setShowPassword, showPassword, icon, button, usePlaceholder, InputProps, inputRef } =
    props;

  const handleFocus = () => (inputRef?.current ? inputRef.current.focus() : {});

  if (isPassword) {
    return {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? <Icon>visibility_off</Icon> : <Icon>visibility</Icon>}
          </IconButton>
        </InputAdornment>
      ),
    };
  }

  const style = { overflow: 'hidden', height: '100%', userSelect: 'none' };
  const styleUsePlaceholder = { flex: 5, maxWidth: 'max-content', ...style };

  if (!isEmptyOrNullObject(icon) || !isEmptyOrNullObject(button)) {
    const { position = 'start', text = '', icon: iconElement } = icon;
    const { icon: iconButton, positionByIcon = position, onClick = () => {} } = button;

    const isIcon = !isEmptyOrNullObject(icon);
    const isButton = !isEmptyOrNullObject(button);

    const isEnd = position === 'end';
    const positionProp = isEnd ? 'endAdornment' : 'startAdornment';

    const handleClick = async () => {
      await onClick();
      handleFocus();
    };

    return {
      ...(isIcon
        ? {
            [positionProp]: (
              <>
                {isButton && isEnd && positionByIcon === 'start' && (
                  <InputAdornment position="end" onClick={handleFocus} style={style}>
                    <IconButton size="small" onClick={handleClick}>
                      {isValidElement(iconButton) ? iconButton : <Icon>{iconButton}</Icon>}
                    </IconButton>
                  </InputAdornment>
                )}

                <InputAdornment position={position} onClick={handleFocus} style={style}>
                  {text ? text : isValidElement(iconElement) ? iconElement : <Icon>{iconElement}</Icon>}
                </InputAdornment>

                {isButton && isEnd && positionByIcon === 'end' && (
                  <InputAdornment position="end" onClick={handleFocus} style={style}>
                    <IconButton size="small" onClick={handleClick}>
                      {isValidElement(iconButton) ? iconButton : <Icon>{iconButton}</Icon>}
                    </IconButton>
                  </InputAdornment>
                )}

                {Boolean(usePlaceholder) && !isEnd && (
                  <InputAdornment
                    position={position}
                    onClick={handleFocus}
                    disabled
                    style={styleUsePlaceholder}
                  >
                    {usePlaceholder}
                  </InputAdornment>
                )}
              </>
            ),
          }
        : {}),

      ...((isIcon ? isEnd : true) && Boolean(usePlaceholder)
        ? {
            startAdornment: (
              <InputAdornment position="start" onClick={handleFocus} disabled style={styleUsePlaceholder}>
                {usePlaceholder}
              </InputAdornment>
            ),
          }
        : {}),

      ...(!isEnd && isButton
        ? {
            endAdornment: (
              <InputAdornment position="end" onClick={handleFocus} style={style}>
                <IconButton size="small" onClick={handleClick}>
                  {isValidElement(iconButton) ? iconButton : <Icon>{iconButton}</Icon>}
                </IconButton>
              </InputAdornment>
            ),
          }
        : {}),
    };
  }

  if (usePlaceholder) {
    return {
      startAdornment: (
        <InputAdornment position="start" onClick={handleFocus} style={styleUsePlaceholder}>
          {usePlaceholder}
        </InputAdornment>
      ),
    };
  }

  return InputProps;
};

TextFieldTK.propTypes = {
  size: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  noSpaces: PropTypes.bool,
  useTrim: PropTypes.bool,
  isNumber: PropTypes.bool,
  isFloat: PropTypes.bool,
  isPassword: PropTypes.bool,
  toUpperCase: PropTypes.bool,
  toLowerCase: PropTypes.bool,
  toCamelCase: PropTypes.bool,
  useCommas: PropTypes.bool,
  maxLength: PropTypes.number,
  maxNumber: PropTypes.number,
  validation: PropTypes.func,
  delay: PropTypes.number,
  icon: PropTypes.shape({
    position: PropTypes.oneOf(['start', 'end']),
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    text: PropTypes.string,
  }),
  button: PropTypes.shape({
    positionByIcon: PropTypes.oneOf(['start', 'end']),
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    onClick: PropTypes.func,
  }),

  others: PropTypes.object,
};

export default TextFieldTK;
